import { ButtonSize, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor, ChipIconPosition } from "@amzn/stencil-react-components/chip";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import IconAlertTriangleFill from "@amzn/stencil-react-icons/icons/icon-alert-triangle-fill";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import TextArea from "@amzn/uxm-stencil-react-components/lib/src/stencil/text-area";
import * as constants from "common/types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { UserPermissions } from "lib/services";
import { debounce } from "lodash";
import React, { FC, useState } from "react";

import { UserAvatar } from "../../UserAvatar";
import { MessageItem } from "../MessageItem";
import { GetCommentsResult } from "../types";

/**
 * Represents the properties for the MessagesView component.
 *
 * @interface
 * @property {string} title - Heading text for the messages view (order display).
 * @property {GetCommentsResult} messages - API result with the list of comments.
 * @property {string} order - Taxonomy ID of the order.
 * @property {string} orderReference - Order reference.
 * @property {(orderTaxId: string, comment: string) => Promise<boolean>} submitComment - Function to post a new comment.
 * @property {boolean} isLoading - True while messages are loading.
 * @property {boolean} isSubmitting - True while a comment is being sent.
 * @property {boolean} hasError - True if an error occurred fetching or posting.
 */
interface MessagesViewProps {
  title: string;
  messages: GetCommentsResult;
  order: string;
  orderReference: string;
  submitComment: (orderTaxId: string, comment: string) => Promise<boolean>;
  isLoading: boolean;
  isSubmitting: boolean;
  hasError: boolean;
}

/**
 * MessagesView
 *
 * Renders a scrollable list of past comments for an order, with:
 * - An error banner when `hasError` is true.
 * - A header showing `title` and `orderReference`.
 * - A "response required" chip if the first message has that flag.
 * - A button to open the reply text area.
 * - A loading spinner when `isLoading` is true.
 * - The list of valid MessageItem rows.
 */
export const MessagesView: FC<MessagesViewProps> = ({
  title,
  messages,
  order,
  orderReference,
  submitComment,
  isLoading,
  isSubmitting,
  hasError,
}) => {
  const [message, setMessage] = useState<string>(constants.EMPTY_STRING);
  const [showMessageField, setShowMessageField] = useState<boolean>(false);
  const username = UserPermissions.current.username;

  const isResponseRequired: boolean = messages?.comments?.at(constants.FIRST_ELEMENT)?.isResponseRequired ?? false;

  const debouncedSendComment = debounce(sendComment, constants.HALF_SECOND_IN_MS);

  function handleSendComment() {
    debouncedSendComment();
  }

  async function sendComment() {
    await submitComment(order, message);
    setMessage(constants.EMPTY_STRING);
  }

  return (
    <>
      <Row>
        <Col
          width={constants.ONE_HUNDRED_PERCENT_STRING}
          gridGap={constants.SPACING_S500}
          padding={{ bottom: constants.SPACING_S500 }}
          minWidth={constants.DEFAULT_FLYOUT_MIN_WIDTH}
        >
          {hasError && (
            <Row>
              <MessageBanner
                dismissButtonAltText="Dismiss"
                hasRoundedCorners={true}
                type={MessageBannerType.Error}
                isDismissible={true}
              >
                {`${getString("errors.general")} ${getString("errors.persistmessage")}`}
                <Link target={constants.BLANK_STRING} href={getString("externalLinks.shopErrorTicketTemplate")}>
                  {getString("errors.ctitext")}
                </Link>
              </MessageBanner>
            </Row>
          )}
          <Row>
            <Col width={constants.ONE_HUNDRED_PERCENT_STRING} gridGap={constants.SPACING_S200}>
              <Row>
                <Text fontSize="T300" fontWeight="bold" id="order-message-item-title">
                  {title}
                </Text>
              </Row>
              <Row>
                <Text id="order-message-order-reference" color="gray">
                  {`${getString("orderMessages.orderNumer")} ${orderReference}`}
                </Text>
              </Row>
            </Col>
          </Row>
          {isResponseRequired && (
            <Row>
              <Chip
                icon={<IconAlertTriangleFill aria-hidden={true} />}
                backgroundColor={ChipBackgroundColor.Yellow}
                iconPosition={ChipIconPosition.Leading}
              >
                {getString("orderMessages.responseRequired")}
              </Chip>
            </Row>
          )}
          <Row justifyContent="flex-end">
            <Button
              aria-disabled={false}
              size={ButtonSize.Small}
              onClick={() => setShowMessageField(true)}
              id="ask-question-button"
              touchpoint="ask-question-button"
              variant={ButtonVariant.Tertiary}
            >
              {getString("orderMessages.askQuestionButton")}
            </Button>
          </Row>

          {(showMessageField || isResponseRequired) && (
            <Row>
              <Col width={constants.ONE_HUNDRED_PERCENT_STRING} gridGap={constants.SPACING_S200}>
                {username && (
                  <Row>
                    <UserAvatar login={username} name={getString("orderMessages.fromSelf")} />
                  </Row>
                )}
                <Row>
                  <TextArea
                    id="question-text-area"
                    width={constants.ONE_HUNDRED_PERCENT_STRING}
                    value={message}
                    touchpoint="question-text-area"
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </Row>
                <Row justifyContent={constants.JUSTIFY_FLEX_END} padding={constants.SPACING_S200}>
                  <Button
                    id="submit-message-button"
                    variant={ButtonVariant.Primary}
                    onClick={handleSendComment}
                    touchpoint="submit-message-button"
                    disabled={!message.trim() || isSubmitting}
                  >
                    {getString("hardwareOrderSupport.submitButton")}
                    {isSubmitting ? <Spinner /> : null}
                  </Button>
                </Row>
              </Col>
            </Row>
          )}
        </Col>
      </Row>

      {isLoading && (
        <Row
          alignItems={constants.ALIGN_ITEMS_CENTER}
          justifyContent={constants.ALIGN_ITEMS_CENTER}
          padding={constants.SPACING_S200}
        >
          {getString("orderMessages.loadingMessages")}
          <Spinner size={SpinnerSize.Small} />
        </Row>
      )}

      {messages?.comments?.map((msg, index) => {
        if (!msg?.posted?.on || !msg?.posted?.by || !msg?.comment) {
          return null;
        }

        return (
          <MessageItem
            timestamp={msg.posted.on}
            username={msg.posted.by}
            message={msg.comment}
            key={`message-${index}`}
            data-testid={`message-${index}`}
            className={index === constants.FIRST_ELEMENT ? "border-line-block" : "border-line-bottom"}
          />
        );
      })}
    </>
  );
};
